import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Control, Controller, ControllerFieldState, ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import { Alert, Button, FormField, Text } from '@legalshield/adonis-ux-framework';

import { FileWithPreview } from '../types';
import { hasForbiddenChars, hasMultipleExtensions } from './filenameValidation';

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  required?: boolean;
};

const MAX_FILES = 20;
const MAX_SIZE_BYTES = 20_000_000;
const MAX_FILES_ERROR = 'You can only upload up to 20 files.';
const MAX_SIZE_ERROR = 'Each file must be smaller than 20MB.';
const MULTIPLE_EXTENSION_ERROR =
  'One or more files appear to have multiple file extensions, which prevents them from uploading. Please rename the file to have a single extension:';
const FORBIDDEN_CHARS_ERROR =
  'One or more files have a name containing characters that are not allowed (/ \\ : * ? " < > |). Please rename the file:';

type InnerProps<T extends FieldValues> = {
  field: ControllerRenderProps<T, Path<T>>;
  fieldState: ControllerFieldState;
  name: Path<T>;
  required?: boolean;
};

const AttachmentsInner = <T extends FieldValues>({ field, fieldState, name, required }: InnerProps<T>) => {
  const current = (field.value as FileWithPreview[] | undefined) ?? [];
  const [alert, setAlert] = useState<string | null>(null);

  const handleDrop = (droppedFiles: File[]) => {
    const forbiddenCharFiles = droppedFiles.filter((f) => hasForbiddenChars(f.name));
    if (forbiddenCharFiles.length > 0) {
      setAlert(`${FORBIDDEN_CHARS_ERROR} ${forbiddenCharFiles.map((f) => f.name).join(', ')}`);
      return;
    }

    const multiExtensionFiles = droppedFiles.filter((f) => hasMultipleExtensions(f.name));
    if (multiExtensionFiles.length > 0) {
      setAlert(`${MULTIPLE_EXTENSION_ERROR} ${multiExtensionFiles.map((f) => f.name).join(', ')}`);
      return;
    }

    if (current.length >= MAX_FILES || droppedFiles.length > MAX_FILES) {
      setAlert(MAX_FILES_ERROR);
      return;
    }
    if (droppedFiles.some((f) => f.size > MAX_SIZE_BYTES)) {
      setAlert(MAX_SIZE_ERROR);
      return;
    }

    const filtered = droppedFiles.filter((df) => !current.some((ca) => df.name === ca.filename));

    const wrapped = filtered.map((file) => {
      const blobUrl = URL.createObjectURL(file);
      return Object.assign(file, {
        preview: blobUrl,
        filename: file.name,
        url: blobUrl,
      }) as FileWithPreview;
    });

    setAlert(null);
    field.onChange([...current, ...wrapped]);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    noClick: true,
    noKeyboard: true,
  });
  const onChooseClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    open();
  };

  return (
    <FormField
      id={String(name)}
      required={required}
      status={fieldState.error ? 'invalid' : undefined}
      validationHint={fieldState.error?.message}
    >
      <>
        {alert ? (
          <Alert
            alertAppearance="error"
            alertType="dismissible"
            onClick={() => setAlert(null)}
            bold
            content={alert}
            icon="alert_information"
          />
        ) : null}
        <Text as="p" style={{ marginBottom: '0.75rem', marginTop: '1rem' }}>
          {`${current.length} Uploaded Document${current.length === 1 ? '' : 's'}`}
        </Text>
        <div
          {...getRootProps({
            className: `rnq-dropzone${isDragActive ? ' rnq-dropzone--active' : ''}`,
          })}
        >
          <input {...getInputProps()} />
          <Text as="p">
            {isDragActive
              ? 'Drop files here'
              : current.length === 0
              ? 'Drag and drop files here, or click the button below'
              : 'Drag and drop more files here, or click the button below'}
          </Text>
          <Button label="Upload a File" variant="standard" iconLeft="action_attach" onClick={onChooseClick} />
        </div>
        {current.length > 0 ? (
          <ul className="rnq-attachment-list">
            {current.map((file, idx) => (
              <li key={`${file.filename}-${idx}`}>
                <span>{file.filename}</span>
                <Button
                  label="Remove"
                  variant="link"
                  onClick={() => field.onChange(current.filter((_, i) => i !== idx))}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </>
    </FormField>
  );
};

const AttachmentsDropzone = <T extends FieldValues>({ control, name, required }: Props<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => (
      <AttachmentsInner field={field} fieldState={fieldState} name={name} required={required} />
    )}
  />
);

export default AttachmentsDropzone;
